/**
 * FRONT-3040: when a PTY terminal ends its session on teardown, and when an
 * open that re-used a live session must repaint.
 *
 * Every terminal but Pro Builder's ends its shell when its component goes away
 * (unmount, page close). Pro Builder's session outlives the tab: it only
 * DETACHES, and the next open attaches to the same shell. The decision is a
 * value OR a function so a caller can say "detach, except when a restart is
 * replacing me" — the function is read at the moment of teardown, never at
 * mount.
 */
export type EndSessionOnTeardown = boolean | (() => boolean);

/** Default (undefined) is true: every other terminal behaves exactly as before. */
export function shouldEndSessionOnTeardown(option: EndSessionOnTeardown | undefined): boolean {
  if (option === undefined) return true;
  return typeof option === 'function' ? option() : option;
}

/**
 * Whether the page-hide handler sends the closing DELETE. A page-hide has no
 * restart in flight, so only the plain boolean decides: a detaching terminal
 * (`false` or a function) sends nothing on page close.
 */
export function shouldEndSessionOnPageHide(option: EndSessionOnTeardown | undefined): boolean {
  return option === undefined || option === true;
}

/**
 * Whether a freshly opened session needs the repaint nudge: the server
 * answered `attached: true` (the person's existing shell was re-used, so a
 * full-screen program has missed what it drew before this stream existed) and
 * the terminal asked for redraws on re-attach. A newly spawned shell
 * (`attached` false/absent) draws its first screen by itself.
 */
export function shouldRepaintOnOpen(attached: boolean | undefined, redrawOnReattach: boolean): boolean {
  return attached === true && redrawOnReattach;
}

/**
 * Ends still in flight. A restart replaces a terminal in one commit, so the
 * new terminal's open request could reach the server before the old
 * terminal's DELETE and attach to the very shell being ended. A terminal that
 * passes `endSessionOnTeardown` records its DELETE here, and waits for any
 * recorded ones before it opens. Terminals that never pass the option neither
 * record nor wait, so their behaviour is unchanged.
 */
const pendingEnds = new Set<Promise<unknown>>();

export function trackPendingEnd(end: Promise<unknown>): void {
  const tracked: Promise<unknown> = end.catch(() => undefined).finally(() => pendingEnds.delete(tracked));
  pendingEnds.add(tracked);
}

export async function waitForPendingEnds(): Promise<void> {
  while (pendingEnds.size > 0) await Promise.all([...pendingEnds]);
}

/**
 * Sends a terminal's open request. For a terminal that passed
 * `endSessionOnTeardown`, the open is itself recorded as pending from the
 * moment it is sent, so a restart's replacement cannot pass
 * `waitForPendingEnds` while the old open is still in flight, attach to the
 * shell it is about to create and then lose it to the old open's late DELETE.
 * It settles when the open has finished and, if the terminal was torn down
 * meanwhile (`isCancelled`), after the DELETE that ends the shell it just
 * created has settled. A detaching terminal sends no DELETE. A terminal that
 * never passed the option fires and forgets the DELETE and records nothing,
 * as before. A failed open rejects to the caller.
 */
export async function openTracked<T>(
  send: () => Promise<T>,
  sendDelete: (opened: T) => Promise<unknown>,
  isCancelled: () => boolean,
  option: EndSessionOnTeardown | undefined,
): Promise<T> {
  let release!: () => void;
  if (option !== undefined) {
    const pending = new Promise<void>((resolve) => { release = resolve; });
    trackPendingEnd(pending);
  }
  try {
    const opened = await send();
    if (isCancelled() && shouldEndSessionOnTeardown(option)) {
      const end = sendDelete(opened).catch(() => {});
      if (option !== undefined) await end;
    }
    return opened;
  } finally {
    release?.();
  }
}
